/**
 * A brief handed from an insight to Content Studio.
 *
 * The dashboard can tell a vendor which product needs pictures; the studio is
 * where pictures get made. This carries the sentence between them so the
 * composer opens already knowing what was asked for, instead of the vendor
 * re-typing what they just read.
 *
 * Held in sessionStorage rather than router state because the two legs of the
 * journey are not one navigation: the vendor lands on the projects list, then
 * chooses a project — possibly an existing one, opened by a card that does its
 * own routing. State would survive the first hop and be lost on the second.
 * A URL param would survive both, but a whole sentence in the vendor's language
 * does not belong in a link someone might paste to a colleague.
 *
 * Session-scoped on purpose: a brief is about what you are doing right now. It
 * should not still be waiting in a tab opened tomorrow.
 */

const KEY = 'ardealy.cs.brief';

/** Briefs older than this are stale — the vendor moved on. */
const TTL_MS = 30 * 60 * 1000;

export interface ContentBrief {
    /** The sentence shown to the vendor and placed in the composer. */
    readonly text: string;
    /** Product the brief is about, when the finding named one. */
    readonly productUuid?: string;
    /**
     * A photo to work from, when the vendor sent one.
     *
     * Already uploaded and already theirs — it arrives as a URL rather than a
     * file because it has been through storage on the way here.
     */
    readonly referenceUrl?: string;
    readonly at: number;
}

/**
 * Every accessor is wrapped: sessionStorage throws outright in some privacy
 * modes, and a failure to hand over a brief must never break navigation to the
 * studio. Losing the brief costs the vendor a sentence of typing; an exception
 * here would cost them the page.
 */
export function putBrief(text: string, productUuid?: string, referenceUrl?: string): void {
    try {
        const brief: ContentBrief = { text, productUuid, referenceUrl, at: Date.now() };
        sessionStorage.setItem(KEY, JSON.stringify(brief));
    } catch {
        /* no brief is better than no page */
    }
}

/** Read without consuming — for showing the vendor what they came to do. */
export function peekBrief(): ContentBrief | null {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;

        const brief = JSON.parse(raw) as ContentBrief;
        if (!brief?.text || typeof brief.at !== 'number') return null;
        if (Date.now() - brief.at > TTL_MS) {
            clearBrief();
            return null;
        }

        return brief;
    } catch {
        return null;
    }
}

/**
 * Read and consume. The composer calls this: a brief that has been placed in
 * front of the vendor has done its job, and leaving it would re-fill the box
 * every time they returned to the project.
 */
export function takeBrief(): ContentBrief | null {
    const brief = peekBrief();
    if (brief) clearBrief();
    return brief;
}

export function clearBrief(): void {
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        /* nothing to do */
    }
}
